import { useCallback, useEffect, useState } from "react";
import {
  type Api,
  ApiError,
  type ImportedCms,
  type ImportedCmsEntry,
} from "../api";
import { BusinessKnowledgeCard } from "./BusinessKnowledge";
import { ImportedCmsPanel } from "./SiteEditor";

/**
 * UX-4 — Content is a first-class view: the imported CMS editor
 * (collections → entries → fields) surfaced out of the advanced editor,
 * plus the business knowledge that grounds the chat assistant.
 */
export function ContentView({ api }: { api: Api }) {
  const [importedCms, setImportedCms] = useState<ImportedCms | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setImportedCms(await api.getImportedCms());
    } catch (caught) {
      // 409 = no site yet; anything else is worth surfacing
      if (!(caught instanceof ApiError && caught.status === 409)) {
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Could not load your content. Try again.",
        );
      }
    } finally {
      setLoaded(true);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateEntry = useCallback(
    async (
      id: string,
      payload: Partial<Pick<ImportedCmsEntry, "data" | "name" | "slug">>,
    ) => {
      setWorking("imported-cms");
      try {
        await api.updateImportedCmsEntry(id, payload);
        await load();
      } finally {
        setWorking(null);
      }
    },
    [api, load],
  );

  const hasCms = (importedCms?.collections?.length ?? 0) > 0;

  return (
    <div className="content-view">
      <section className="card">
        <div className="card-head">
          <div>
            <h2>Content</h2>
            <p className="muted">
              Structured content imported with your site — edit an entry and the
              change flows to the next publish.
            </p>
          </div>
        </div>
        {error && <p className="notice">{error}</p>}
        {!error && loaded && !hasCms && (
          <p className="muted">
            No structured content yet. Content appears here when your imported
            site brings collections with it (services, team members, articles
            and similar).
          </p>
        )}
        {hasCms && (
          <ImportedCmsPanel
            importedCms={importedCms}
            onUpdateEntry={updateEntry}
            working={working}
          />
        )}
      </section>
      <BusinessKnowledgeCard api={api} />
    </div>
  );
}
